import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { label: "Dashboard", icon: "ri-dashboard-line", path: "/admin/dashboard" },
  { label: "Products", icon: "ri-gift-line", path: "/admin/products" },
  { label: "Categories", icon: "ri-folder-line", path: "/admin/categories" },
  { label: "Contact Messages", icon: "ri-mail-line", path: "/admin/contacts" },
  { label: "Newsletter", icon: "ri-mail-send-line", path: "/admin/newsletter" },
];

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/admin/login");
      } else {
        setUserEmail(session.user.email || "");
        setAuthChecked(true);
      }
    });
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  const isActive = (path: string) => location.pathname === path;

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-100 z-40 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:z-auto`}
      >
        <div className="h-16 flex items-center px-5 border-b border-gray-100 gap-3">
          <div className="flex items-center justify-center rounded-xl bg-gradient-to-br from-[#3a8cbf] to-[#e05a8a] w-8 h-8 flex-shrink-0">
            <i className="ri-gift-2-line text-white text-base" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 leading-none">Gift Mantra</p>
            <p className="text-xs text-gray-400 mt-0.5">Admin Panel</p>
          </div>
        </div>

        <nav className="flex-1 p-4 flex flex-col gap-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-3 mb-2">Management</p>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isActive(item.path) ? "text-gray-900 font-semibold" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${isActive(item.path) ? "bg-gradient-to-br from-[#3a8cbf] to-[#e05a8a]" : "bg-gray-100"}`}>
                <i className={`${item.icon} text-sm ${isActive(item.path) ? "text-white" : "text-gray-500"}`} />
              </div>
              <span>{item.label}</span>
              {isActive(item.path) && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]" />}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-800 cursor-pointer transition-colors whitespace-nowrap">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
              <i className="ri-external-link-line text-gray-500 text-sm" />
            </div>
            View Live Site
          </a>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-colors whitespace-nowrap mt-1">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 flex-shrink-0">
              <i className="ri-logout-box-line text-red-500 text-sm" />
            </div>
            Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-100 flex items-center px-6 gap-4">
          <button className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-colors" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <i className="ri-menu-line text-gray-600 text-lg" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#3a8cbf] to-[#e05a8a] flex-shrink-0">
              <i className="ri-user-line text-white text-sm" />
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-gray-900 leading-none">Admin</p>
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[160px]">{userEmail}</p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
